import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const STATUS_LABEL = {
  approved: '입고대기', pickup_pending: '회수중', pickup_in_progress: '회수중',
  received: '검수대기', inspecting: '검수중', inspected: '검수완료',
  exchange_preparing: '교환피킹', exchange_shipped: '교환출고', completed: '완료',
};
const INSPECT_RESULTS = [
  ['RESTOCKABLE',   '정상 재판매'],
  ['DEFECTIVE',     '불량'],
  ['DAMAGED',       '파손'],
  ['SELLER_RETURN', '판매자 반환'],
  ['DISCARD',       '폐기'],
  ['UNKNOWN',       '판단필요'],
];
const TABS = [['all', '전체'], ['approved', '입고대기'], ['received', '검수대기'],
  ['exchange_preparing', '교환피킹'], ['exchange_shipped', '교환출고'],
  ['completed', '완료']];

export default function OperatorClaims() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inspection, setInspection] = useState({});
  const [tracking, setTracking] = useState(''); const [courier, setCourier] = useState('');
  // STEP 3-WMS-A · Return Putaway state · STEP 3-WMS-A-HOTFIX: server-driven progress SoT.
  const [locations, setLocations] = useState([]);
  const [progress, setProgress] = useState(null);       // {items:[{item_index, restocked_qty, already_putaway_qty, remaining_putaway_qty, global_unlocated_stock, ...}]}
  const [putawayForm, setPutawayForm] = useState({});   // { [item_index]: {location_id, quantity, client_ref, submitting} }

  const load = async () => {
    try {
      const r = await api.get('/operator/claims');
      setRows(r.data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('물류직원 권한이 없습니다.');
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (tab === 'all') return true;
    return r.rma_status === tab;
  }), [rows, tab]);

  const openDetail = async (rmaId) => {
    try { const r = await api.get(`/operator/claims/${rmaId}`);
      setDetail(r.data); setInspection({});
      setTracking(''); setCourier('');
      setPutawayForm({}); setProgress(null); setLocations([]);
    } catch { toast.error('상세 불러오기 실패'); }
  };

  // STEP 3-WMS-A-HOTFIX · Fresh client_ref per submission (retry-safe UUID).
  const _newClientRef = () => (
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );

  // STEP 3-WMS-A-HOTFIX · Backend is Source of Truth for per-line progress.
  // No frontend re-computation of restocked_qty / already_putaway_qty.
  const loadProgress = async (rmaId) => {
    try {
      const r = await api.get(`/operator/rma-putaway-progress/${rmaId}`);
      setProgress(r.data);
      return r.data;
    } catch (e) {
      if (e?.response?.status === 400) setProgress({ items: [] });
      else if (e?.response?.status !== 403) toast.error('반품 적치 정보를 불러오지 못했습니다.');
      return null;
    }
  };

  // Load progress + warehouse locations when a restock-applied claim opens.
  useEffect(() => {
    if (!detail?.rma_id || !detail?.restock_applied) return;
    let cancelled = false;
    (async () => {
      const p = await loadProgress(detail.rma_id);
      if (cancelled || !p) return;
      if (!detail.warehouse_id) { setLocations([]); return; }
      try {
        const r = await api.get('/operator/warehouse-locations',
          { params: { warehouse_id: detail.warehouse_id } });
        if (!cancelled) setLocations((r.data.items || []).filter((l) => l.is_active));
      } catch { if (!cancelled) setLocations([]); }
    })();
    return () => { cancelled = true; };
  }, [detail?.rma_id, detail?.restock_applied, detail?.warehouse_id]);

  const eligibleRows = useMemo(
    () => ((progress?.items || []).filter((r) => r.eligible && r.restocked_qty > 0)),
    [progress]);

  const submitReturnPutaway = async (row) => {
    const form = putawayForm[row.item_index] || {};
    const loc = (form.location_id || '').trim();
    const qty = Math.floor(Number(form.quantity || 0));
    if (!loc) return toast.error('적치 위치를 선택하세요.');
    if (!(qty > 0)) return toast.error('수량은 1 이상이어야 합니다.');
    if (qty > row.remaining_putaway_qty) return toast.error(`반품 잔여 수량 초과 · 최대 ${row.remaining_putaway_qty}`);
    if (qty > row.global_unlocated_stock) return toast.error(`전체 미배치 재고 초과 · 최대 ${row.global_unlocated_stock}`);
    // Retry-safe client_ref: generated ONCE per submission attempt.
    // If the user re-clicks after a failure, that is a NEW submission → new client_ref.
    // Network-layer retries of a SINGLE axios call reuse the same client_ref.
    const client_ref = form.client_ref || _newClientRef();
    setPutawayForm((f) => ({ ...f, [row.item_index]: { ...form, client_ref, submitting: true } }));
    try {
      const r = await api.post('/operator/return-putaway', {
        rma_id:      detail.rma_id,
        item_index:  row.item_index,
        location_id: loc,
        quantity:    qty,
        client_ref,
      });
      if (r.data?.already_applied) {
        toast('이미 처리된 요청입니다.', { icon: 'ℹ️' });
      } else {
        toast.success(`반품 적치 완료 · +${r.data.quantity_placed}`);
      }
      // Fresh client_ref for the next placement on this row.
      setPutawayForm((f) => ({ ...f, [row.item_index]: { location_id: loc, quantity: '', client_ref: '', submitting: false } }));
      await loadProgress(detail.rma_id);
    } catch (e) {
      const s = e?.response?.status;
      const msg = e?.response?.data?.detail;
      if (s === 403) toast.error('이 작업을 처리할 권한이 없습니다.');
      else if (s === 409) toast.error(msg || '반품 잔여/미배치 재고를 초과했습니다.');
      else if (s === 404) toast.error(msg || 'Location 을 찾을 수 없습니다.');
      else toast.error(msg || '반품 적치 실패');
      // Keep client_ref so a network retry from the user re-uses the same idempotency key.
      setPutawayForm((f) => ({ ...f, [row.item_index]: { ...form, client_ref, submitting: false } }));
    }
  };

  const doAction = async (action, payload) => {
    setBusy(true);
    try {
      await api.post(`/operator/claims/${detail.rma_id}/${action}`, payload || {});
      toast.success('처리되었습니다.'); await openDetail(detail.rma_id); await load();
    } catch (e) {
      const detailMsg = e?.response?.data?.detail || '';
      if (detailMsg.includes('OUT_OF_STOCK')) toast.error(`❌ 재고 부족 · ${detailMsg}`);
      else toast.error(detailMsg || '처리 실패');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="operator-claims-page">
        <h1 className="text-lg font-semibold mb-4">반품/교환 작업</h1>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="operator-claims-tabs">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
                     className={`px-3 py-1 text-sm rounded ${tab === v ? 'bg-neutral-900 text-white' : 'bg-neutral-100'}`}
                     data-testid={`operator-claims-tab-${v}`}>{l}</button>
          ))}
        </div>

        <div className="border rounded divide-y" data-testid="operator-claims-list">
          {filtered.length === 0 && <div className="p-4 text-sm text-neutral-500">해당 항목이 없습니다.</div>}
          {filtered.map(r => (
            <button key={r.rma_id} onClick={() => openDetail(r.rma_id)}
                     className="w-full text-left p-3 hover:bg-neutral-50 flex items-center justify-between"
                     data-testid={`operator-claim-row-${r.rma_id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {r.rma_id} · {r.claim_type}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  주문 {r.seller_order_id} · {r.items?.length || 0}개 상품
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-neutral-100 rounded">
                {STATUS_LABEL[r.rma_status] || r.rma_status}
              </span>
            </button>
          ))}
        </div>

        {detail && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
                onClick={() => setDetail(null)} data-testid="operator-claim-detail-modal">
            <div className="bg-white rounded max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
                  onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm text-neutral-500">Claim {detail.rma_id}</div>
                  <div className="text-lg font-semibold">
                    {detail.claim_type} · {STATUS_LABEL[detail.rma_status] || detail.rma_status}
                  </div>
                </div>
                <button onClick={() => setDetail(null)} className="text-neutral-500"
                         data-testid="operator-claim-close">✕</button>
              </div>

              <dl className="grid grid-cols-2 gap-2 text-sm mb-4">
                <dt className="text-neutral-500">주문</dt><dd>{detail.seller_order_id}</dd>
                <dt className="text-neutral-500">사유</dt><dd>{detail.reason_code}</dd>
                <dt className="text-neutral-500">회수방법</dt><dd>{detail.pickup_method || '-'}</dd>
                <dt className="text-neutral-500">수령인</dt>
                <dd>{detail.shipping_address_snapshot?.recipient_name || '-'}</dd>
                <dt className="text-neutral-500">전화</dt>
                <dd>{detail.shipping_address_snapshot?.phone || '-'}</dd>
                <dt className="text-neutral-500">주소</dt>
                <dd>
                  {detail.shipping_address_snapshot?.address1}
                  {' '}{detail.shipping_address_snapshot?.address2}
                </dd>
              </dl>

              <div className="text-xs text-neutral-500 mb-1">반품 상품</div>
              <div className="border rounded divide-y mb-3 text-sm">
                {(detail.items || []).map((it, i) => {
                  const key = it.seller_order_item_id || it.product_id;
                  return (
                    <div key={i} className="p-2 grid grid-cols-2 gap-2 items-center">
                      <div>
                        <div>{it.name_snapshot || it.product_id}</div>
                        <div className="text-xs text-neutral-500">{it.sku} · {it.claim_quantity}개</div>
                      </div>
                      {detail.rma_status === 'received' && (
                        <select value={inspection[key] || ''}
                                 onChange={e => setInspection(s => ({ ...s, [key]: e.target.value }))}
                                 className="border rounded px-2 py-1 text-sm w-full"
                                 data-testid={`operator-inspect-${key}`}>
                          <option value="">검수결과 선택</option>
                          {INSPECT_RESULTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>

              {detail.rma_status === 'approved' && (
                <button onClick={() => doAction('receive')} disabled={busy}
                         className="w-full py-2 bg-emerald-600 text-white text-sm rounded disabled:opacity-50"
                         data-testid="operator-receive-btn">입고 처리</button>
              )}
              {detail.rma_status === 'received' && (
                <button onClick={() => {
                  const items = Object.entries(inspection).filter(([, v]) => v).map(([k, v]) => {
                    const it = (detail.items || []).find(x => (x.seller_order_item_id || x.product_id) === k);
                    return { seller_order_item_id: it.seller_order_item_id,
                              product_id: it.product_id,
                              quantity: it.claim_quantity, result: v };
                  });
                  if (items.length === 0) return toast.error('검수결과를 선택하세요.');
                  const overall = items.every(x => x.result === 'RESTOCKABLE') ? 'RESTOCKABLE' :
                                    items.every(x => x.result === 'DEFECTIVE') ? 'DEFECTIVE' : 'UNKNOWN';
                  doAction('inspect', { overall_result: overall, items });
                }} disabled={busy}
                         className="w-full py-2 bg-emerald-600 text-white text-sm rounded disabled:opacity-50"
                         data-testid="operator-inspect-submit">검수 완료</button>
              )}
              {detail.claim_type === 'exchange' && detail.rma_status === 'inspected' && (
                <button onClick={() => doAction('exchange-pick')} disabled={busy}
                         className="w-full py-2 bg-indigo-600 text-white text-sm rounded disabled:opacity-50"
                         data-testid="operator-exchange-pick">교환 상품 피킹</button>
              )}
              {detail.claim_type === 'exchange' && detail.rma_status === 'exchange_preparing' && (
                <div className="space-y-2 border-t pt-3">
                  <input type="text" placeholder="교환 재출고 송장번호"
                          value={tracking} onChange={e => setTracking(e.target.value)}
                          className="border rounded px-2 py-1 w-full text-sm"
                          data-testid="operator-exchange-tracking" />
                  <input type="text" placeholder="택배사(cj/hanjin/... 또는 직접입력)"
                          value={courier} onChange={e => setCourier(e.target.value)}
                          className="border rounded px-2 py-1 w-full text-sm"
                          data-testid="operator-exchange-courier" />
                  <button onClick={() => tracking &&
                            doAction('exchange-ship', { tracking_number: tracking, courier })}
                           disabled={busy || !tracking}
                           className="w-full py-2 bg-indigo-600 text-white text-sm rounded disabled:opacity-50"
                           data-testid="operator-exchange-ship">교환 재출고</button>
                </div>
              )}

              {/* STEP 3-WMS-A-HOTFIX · Return Putaway (per-line remaining/idempotency guards) */}
              {eligibleRows.length > 0 && (
                <div className="mt-4 border-t pt-3 space-y-3" data-testid="operator-return-putaway">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-sm font-bold">반품 적치 (Return Putaway)</h3>
                    <span className="text-[11px] text-neutral-500">RESTOCK 완료 상품을 실제 선반에 재배치</span>
                  </div>
                  {!detail.warehouse_id && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="return-putaway-no-warehouse">
                      warehouse_id 가 지정되지 않은 claim 입니다. 판매자 승인 단계에서 창고를 지정한 뒤 적치할 수 있습니다.
                    </div>
                  )}
                  {detail.warehouse_id && locations.length === 0 && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="return-putaway-no-locations">
                      활성 Location 이 없습니다. 창고 담당자에게 문의하세요.
                    </div>
                  )}
                  {eligibleRows.map((row) => {
                    const form = putawayForm[row.item_index] || {};
                    const submitting = Boolean(form.submitting);
                    const optionStr = Object.entries(row.option || {}).map(([k, v]) => `${k}=${v}`).join(' · ') || '-';
                    const maxPlaceable = Math.min(row.remaining_putaway_qty, row.global_unlocated_stock);
                    const done = row.remaining_putaway_qty === 0;
                    return (
                      <div key={row.item_index} className="rounded border border-neutral-200 p-3 space-y-2"
                            data-testid={`return-putaway-row-${row.item_index}`}>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          <div><span className="text-neutral-500">상품</span> · <b>{row.name_snapshot || row.product_id}</b></div>
                          <div><span className="text-neutral-500">옵션</span> · {optionStr}</div>
                          <div><span className="text-neutral-500">SKU</span> · <span className="font-mono">{row.sku || '-'}</span></div>
                          <div><span className="text-neutral-500">variant_key</span> · <span className="font-mono">{row.variant_key}</span></div>
                          <div><span className="text-neutral-500">반품 RESTOCK 수량</span> · <b data-testid={`return-putaway-restock-qty-${row.item_index}`}>{row.restocked_qty}</b></div>
                          <div><span className="text-neutral-500">이미 적치</span> · <b data-testid={`return-putaway-already-${row.item_index}`}>{row.already_putaway_qty}</b></div>
                          <div><span className="text-neutral-500">남은 적치</span> · <b className={done ? 'text-neutral-400' : 'text-emerald-700'} data-testid={`return-putaway-remaining-${row.item_index}`}>{row.remaining_putaway_qty}</b></div>
                          <div><span className="text-neutral-500">전체 미배치 재고</span> · <b data-testid={`return-putaway-global-unlocated-${row.item_index}`}>{row.global_unlocated_stock}</b></div>
                        </div>
                        {done ? (
                          <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded px-2 py-1" data-testid={`return-putaway-done-${row.item_index}`}>
                            [적치 완료] · 이 반품 라인의 모든 수량이 적치되었습니다.
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-end gap-2">
                            <label className="flex flex-col text-xs gap-0.5 flex-1 min-w-[180px]">
                              <span className="text-neutral-500">적치 위치</span>
                              <select value={form.location_id || ''}
                                       onChange={(e) => setPutawayForm((f) => ({ ...f, [row.item_index]: { ...(f[row.item_index] || {}), location_id: e.target.value } }))}
                                       disabled={submitting || locations.length === 0 || !detail.warehouse_id}
                                       className="border rounded px-2 py-1 text-sm disabled:bg-neutral-100"
                                       data-testid={`return-putaway-location-${row.item_index}`}>
                                <option value="">-- 선택 --</option>
                                {locations.map((l) => (
                                  <option key={l.location_id} value={l.location_id}>
                                    {l.code}{l.label ? ` · ${l.label}` : ''} ({l.location_type}){l.barcode ? ` · BC ${l.barcode}` : ''}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="flex flex-col text-xs gap-0.5 w-28">
                              <span className="text-neutral-500">수량 (max {maxPlaceable})</span>
                              <input type="number" min={1} max={maxPlaceable}
                                      value={form.quantity ?? ''}
                                      onChange={(e) => setPutawayForm((f) => ({ ...f, [row.item_index]: { ...(f[row.item_index] || {}), quantity: e.target.value } }))}
                                      disabled={submitting}
                                      className="border rounded px-2 py-1 text-sm disabled:bg-neutral-100"
                                      data-testid={`return-putaway-qty-${row.item_index}`} />
                            </label>
                            <button onClick={() => submitReturnPutaway(row)}
                                     disabled={submitting || !form.location_id || !form.quantity || maxPlaceable === 0}
                                     className="px-3 py-1.5 bg-[#FF3B30] text-white rounded text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
                                     data-testid={`return-putaway-submit-${row.item_index}`}>
                              {submitting && <Loader2 size={12} className="animate-spin" />}
                              적치 완료
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
